import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Body2, Header2, Button, ListSeparator } from "@activecollab/components";
import PropTypes from "prop-types";
import AddBillingAddressDialog from "~/components/Subscription/BillingPaymentMethod/AddBillingAddressDialog";
import { getBillingAddress } from "~/store/selectors/selector.billingPaymentMethod";

const BillingDetails = ({ isCanceled }) => {
  const billingDetails = useSelector(getBillingAddress);
  const { t } = useTranslation();
  const [showDialog, setShowDialog] = useState(false);
  const openDialog = useCallback(() => {
    setShowDialog(true);
  }, []);
  const closeDialog = useCallback(() => {
    setShowDialog(false);
  }, []);

  return (
    <div className="tw-mb-14">
      <div className="tw-flex tw-justify-between">
        <Header2
          color="primary"
          data-testid="billingDetailsLabel"
          className="tw-pt-1"
        >
          {t("Billing Details")}
        </Header2>
        <Button
          variant="text colored"
          className="tw-mb-1"
          onClick={openDialog}
          disabled={isCanceled}
        >
          {t("Change Billing Details")}
        </Button>
        {showDialog ? (
          <AddBillingAddressDialog
            open={showDialog}
            onClose={closeDialog}
            onSubmit={closeDialog}
          />
        ) : null}
      </div>
      <ListSeparator className="tw-mx-0 tw-mt-0 tw-mb-6" />
      <div className="tw-flex tw-flex-col md:tw-grid md:tw-grid-cols-[200px_auto] md:tw-grid-rows-7 tw-gap-2">
        <Body2
          color="primary"
          weight="bold"
          data-testid="billingDetailsNameLabel"
        >
          {t("Name")}
        </Body2>
        <Body2
          color="tertiary"
          data-testid="billingDetailsNameValue"
        >
          {billingDetails.name}
        </Body2>
        <Body2
          color="primary"
          weight="bold"
          data-testid="billingDetailsEmailLabel"
        >
          {t("Email")}
        </Body2>
        <Body2
          color="tertiary"
          data-testid="billingDetailsEmailValue"
        >
          {billingDetails.email}
        </Body2>
        {billingDetails.vatId ? (
          <>
            <Body2
              color="primary"
              weight="bold"
              data-testid="billingDetailsVatLabel"
            >
              {t("VAT ID")}
            </Body2>
            <Body2
              color="tertiary"
              data-testid="billingDetailsVatValue"
            >
              {billingDetails.vatId}
            </Body2>
          </>
        ) : null}
        <Body2
          color="primary"
          weight="bold"
          className="tw-mr-8 tw-row-start-4 tw-row-end-7"
          data-testid="billingDetailsAddressLabel"
        >
          {t("Address")}
        </Body2>
        <div className="tw-row-start-4 tw-row-end-7">
          <Body2
            color="tertiary"
            data-testid="billingDetailsAddressValue"
          >
            {billingDetails.address}
          </Body2>
          <Body2
            color="tertiary"
            data-testid="billingDetailsAddressZipCodeAndCity"
          >
            {billingDetails.city} {billingDetails.zipCode}
            {billingDetails.region ? <>, {billingDetails.region}</> : null}
          </Body2>
          <Body2
            color="tertiary"
            data-testid="billingDetailsAddressCountry"
          >
            {billingDetails.country}
          </Body2>
        </div>
      </div>
    </div>
  );
};

BillingDetails.propTypes = {
  isCanceled: PropTypes.bool,
};

export default BillingDetails;
